import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token.
//
// PRD-008 — operator impersonation: when an impersonation session is
// active (operatorImpersonationToken in sessionStorage), customer-facing
// requests use the impersonation token; operator-console requests
// (/api/internal/*) keep using the regular operator accessToken so the
// operator surface remains reachable from any page during impersonation.
api.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const impersonationToken = sessionStorage.getItem('operatorImpersonationToken');
    const accessToken = localStorage.getItem('accessToken');

    if (impersonationToken && !url.startsWith('/api/internal/')) {
      config.headers.Authorization = `Bearer ${impersonationToken}`;
    } else if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  logout: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return api.post('/api/auth/logout', { refreshToken });
  },
  refresh: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return api.post('/api/auth/refresh', { refreshToken });
  },
  me: () => api.get('/api/auth/me'),
  invite: (data) => api.post('/api/auth/invite', data),
  // PRD-007 — per-user notification preferences (paused boolean v1).
  getNotificationPreferences: () => api.get('/api/auth/notification-preferences'),
  setNotificationPreferences: (preferences) =>
    api.put('/api/auth/notification-preferences', { preferences }),
};

// Workspaces API
export const workspacesApi = {
  list: () => api.get('/api/workspaces'),
  get: (id) => api.get(`/api/workspaces/${id}`),
  create: (data) => api.post('/api/workspaces', data),
  switch: (workspaceId) => api.post('/api/workspaces/switch', { workspaceId }),
};

// Connections API
export const connectionsApi = {
  list: () => api.get('/api/connections'),
  get: (id) => api.get(`/api/connections/${id}`),
  create: (data) => api.post('/api/connections', data),
  accept: (id) => api.post(`/api/connections/${id}/accept`),
  sever: (id) => api.post(`/api/connections/${id}/sever`),
};

// Buyer portfolio API — cross-vendor thin wedge (PRD §6 / §9 Phase 1).
export const buyerPortfolioApi = {
  list: () => api.get('/api/buyer-portfolio'),
};

// PRD-016 — single dual-sign API. The seller's signature auto-shares; the
// separate share-with-buyer step is removed. (Renamed from proposalSigningApi
// to drop the removed shareWithBuyer surface.)
export const signingApi = {
  sign: (proposalId, body = {}) => api.post(`/api/proposals/${proposalId}/sign`, body),
};

// Verification (Cut B). PRD-015: the agent run moved to /run-verification
// (the /verify path is now the seller manual override).
export const verificationApi = {
  // Agent run (Token Broker verification agent).
  runAgent: (agreementId, deliverableId) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/run-verification`),
  // Seller manual verification override (note ≥ 10).
  verifyManual: (agreementId, deliverableId, note) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/verify`, { note }),
  // Buyer sign-off (PRD-021 WU-5) — append-only events, idempotent.
  accept: (agreementId, deliverableId, idempotencyKey) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/accept`, { idempotency_key: idempotencyKey }),
  refute: (agreementId, deliverableId, note, idempotencyKey) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/refute`, { note, idempotency_key: idempotencyKey }),
  listEvidence: (agreementId, deliverableId, params = {}) =>
    api.get(`/api/agreements/${agreementId}/deliverables/${deliverableId}/evidence`, { params }),
  // Backward-compat alias (old callers used verify→agent run).
  verify: (agreementId, deliverableId) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/run-verification`),
};

// Connector grants API (PRD-003 Cut B).
export const connectorGrantsApi = {
  list: (agreementId) => api.get(`/api/agreements/${agreementId}/connectors/grants`),
  create: (agreementId, { source_system, oauth_code }) =>
    api.post(`/api/agreements/${agreementId}/connectors/grants`, { source_system, oauth_code }),
  setScope: (agreementId, grantId, channel_ids) =>
    api.put(`/api/agreements/${agreementId}/connectors/grants/${grantId}/scope`, { channel_ids }),
  revoke: (agreementId, grantId) =>
    api.delete(`/api/agreements/${agreementId}/connectors/grants/${grantId}`),
};

// Outcome Spec API (PRD-003).
//
// Three contexts:
//   - Proposal-side authoring (mutable while the proposal is open).
//   - Agreement-side reading (locked at signing; markdown rendered).
//   - Amendment-draft authoring (mutable while the amendment is in flight).
// PRD-027 WU-C — buyer Suggestions (redline) on a proposal.
export const proposalSuggestionsApi = {
  list: (proposalId) => api.get(`/api/proposals/${proposalId}/suggestions`),
  create: (proposalId, body) => api.post(`/api/proposals/${proposalId}/suggestions`, body),
  comment: (proposalId, sid, body) => api.post(`/api/proposals/${proposalId}/suggestions/${sid}/comments`, body),
  accept: (proposalId, sid) => api.post(`/api/proposals/${proposalId}/suggestions/${sid}/accept`, {}),
  reject: (proposalId, sid, note) => api.post(`/api/proposals/${proposalId}/suggestions/${sid}/reject`, note ? { note } : {}),
  withdraw: (proposalId, sid) => api.post(`/api/proposals/${proposalId}/suggestions/${sid}/withdraw`, {}),
  abandonAll: (proposalId) => api.post(`/api/proposals/${proposalId}/suggestions/abandon-all`, {}),
};

export const outcomeSpecsApi = {
  // Proposal-side
  getProposalSpec: (proposalId, deliverableId) =>
    api.get(`/api/proposals/${proposalId}/deliverables/${deliverableId}/spec`),
  // PRD-028 F1 — direct seller content save (the legacy proposal-side block
  // propose/accept/reject negotiation is retired; the buyer redlines only via
  // Suggestions). Writes content_markdown only, no state transition.
  saveBlock: (proposalId, deliverableId, blockId, body) =>
    api.put(
      `/api/proposals/${proposalId}/deliverables/${deliverableId}/spec/blocks/${blockId}`,
      body
    ),
  addArtifactRequirement: (proposalId, deliverableId, body) =>
    api.post(
      `/api/proposals/${proposalId}/deliverables/${deliverableId}/spec/artifact-requirements`,
      body
    ),
  removeArtifactRequirement: (proposalId, deliverableId, reqId) =>
    api.delete(
      `/api/proposals/${proposalId}/deliverables/${deliverableId}/spec/artifact-requirements/${reqId}`
    ),

  // Agreement-side (read-only)
  getAgreementSpec: (agreementId, deliverableId) =>
    api.get(`/api/agreements/${agreementId}/deliverables/${deliverableId}/spec`),
  getAgreementSpecVersions: (agreementId, deliverableId) =>
    api.get(`/api/agreements/${agreementId}/deliverables/${deliverableId}/spec/versions`),

  // Amendment lifecycle
  createAmendment: (agreementId) =>
    api.post(`/api/agreements/${agreementId}/amendments`),
  signAmendment: (agreementId, amendmentId) =>
    api.post(`/api/agreements/${agreementId}/amendments/${amendmentId}/sign`),
  cancelAmendment: (agreementId, amendmentId) =>
    api.post(`/api/agreements/${agreementId}/amendments/${amendmentId}/cancel`),

  // Amendment-draft mutations
  getAmendmentDraftSpec: (agreementId, amendmentId, deliverableId) =>
    api.get(
      `/api/agreements/${agreementId}/amendments/${amendmentId}/deliverables/${deliverableId}/spec`
    ),
  proposeAmendmentBlock: (agreementId, amendmentId, deliverableId, blockId, body) =>
    api.post(
      `/api/agreements/${agreementId}/amendments/${amendmentId}/deliverables/${deliverableId}/spec/blocks/${blockId}/propose`,
      body
    ),
  acceptAmendmentBlock: (agreementId, amendmentId, deliverableId, blockId, body = {}) =>
    api.post(
      `/api/agreements/${agreementId}/amendments/${amendmentId}/deliverables/${deliverableId}/spec/blocks/${blockId}/accept`,
      body
    ),
};

// Phase 1.5 — AI Assist (drafting only). Workspace-allowlist gated;
// non-allowlisted workspaces get 404 from every endpoint here. The
// frontend treats 404 as "feature unavailable" and hides AI Assist
// affordances accordingly. Apply lands via outcomeSpecsApi.saveBlock,
// not these endpoints.
// PRD-016: AI Assist proposal drafting + the Bookings UI are removed; their
// API clients (aiAssistApi, bookingsApi) are deleted.

// Deliverable state machine API — v2 verb-specific routes. Replaces the
// PUT /agreements/:id/deliverables/:id path (returns 410 in v2).
export const deliverablesApi = {
  // PRD-015 — 3-state machine. Seller manual override (defined→verified).
  manualVerify: (agreementId, deliverableId, note) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/verify`, { note }),
  // Run the verification agent (defined→verified on a `verified` verdict).
  runVerification: (agreementId, deliverableId) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/run-verification`),
  listEvents: (agreementId, deliverableId) =>
    api.get(`/api/agreements/${agreementId}/deliverables/${deliverableId}/events`),
  appendEvent: (agreementId, deliverableId, body) =>
    api.post(`/api/agreements/${agreementId}/deliverables/${deliverableId}/events`, body),
  aggregatedStream: (agreementId) => api.get(`/api/agreements/${agreementId}/events`),
};

// Connection invitations API
// Public lookup / accept-by-token use bare axios so they aren't gated by
// the Authorization header for anonymous signup.
export const connectionInvitationsApi = {
  create: (data) => api.post('/api/connection-invitations', data),
  lookup: (token) => axios.get(`${API_BASE_URL}/api/connection-invitations/${token}`),
  accept: (token, body, { authToken } = {}) =>
    axios.post(`${API_BASE_URL}/api/connection-invitations/${token}/accept`, body || {}, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    }),
  // PRD-010 F-PI-1..3 — seller-side pending invitations panel (email-link flow).
  listSent: () => api.get('/api/connection-invitations', { params: { status: 'sent' } }),
  regenerateUrl: (id) => api.post(`/api/connection-invitations/${id}/regenerate-url`),
  revoke: (id) => api.post(`/api/connection-invitations/${id}/revoke`),
  // PRD-016: in-app directory invite + receiving tray (sendToWorkspace,
  // listReceived, acceptInApp, declineInApp) removed — email-link only.
};

// Outcome Types API (legacy — back-compat one cycle; new code should use
// deliverableTemplatesApi below, which is the canonical surface post-WU 1-4).
export const outcomeTypesApi = {
  list: (params) => api.get('/api/outcome-types', { params }),
  get: (id) => api.get(`/api/outcome-types/${id}`),
  create: (data) => api.post('/api/outcome-types', data),
  update: (id, data) => api.put(`/api/outcome-types/${id}`, data),
  delete: (id) => api.delete(`/api/outcome-types/${id}`),
  addDeliverable: (id, data) => api.post(`/api/outcome-types/${id}/deliverables`, data),
  updateDeliverable: (id, deliverableId, data) => api.put(`/api/outcome-types/${id}/deliverables/${deliverableId}`, data),
  deleteDeliverable: (id, deliverableId) => api.delete(`/api/outcome-types/${id}/deliverables/${deliverableId}`),
};

// Proposal Comments API (Outcome Setup Redesign WU 1-3).
// Typed-payload comments for buyer collaboration: comment /
// field_suggestion / suggested_addition + a resolve endpoint.
export const proposalCommentsApi = {
  list: (proposalId) => api.get(`/api/proposals/${proposalId}/comments`),
  create: (proposalId, body) =>
    api.post(`/api/proposals/${proposalId}/comments`, body),
  resolve: (proposalId, commentId, body) =>
    api.post(`/api/proposals/${proposalId}/comments/${commentId}/resolve`, body),
};

// Deliverable Templates API (Outcome Setup Redesign WU 1-4).
// One row per deliverable template, optionally enriched with seeded
// PRD-003 spec block content.
export const deliverableTemplatesApi = {
  list: (workspaceId) =>
    api.get(`/api/workspaces/${workspaceId}/deliverable-templates`),
  create: (workspaceId, data) =>
    api.post(`/api/workspaces/${workspaceId}/deliverable-templates`, data),
  update: (workspaceId, templateId, data) =>
    api.put(`/api/workspaces/${workspaceId}/deliverable-templates/${templateId}`, data),
  archive: (workspaceId, templateId) =>
    api.delete(`/api/workspaces/${workspaceId}/deliverable-templates/${templateId}`),
};

// Proposals API
export const proposalsApi = {
  list: (params) => api.get('/api/proposals', { params }),
  get: (id) => api.get(`/api/proposals/${id}`),
  create: (data) => api.post('/api/proposals', data),
  update: (id, data) => api.put(`/api/proposals/${id}`, data),
  // PRD-020 F2: hard-delete an unsigned Proposal (409 if signed).
  delete: (id) => api.delete(`/api/proposals/${id}`),
  updateStatus: (id, status) => api.post(`/api/proposals/${id}/status`, { status }),
  addOutcome: (proposalId, data) => api.post(`/api/proposals/${proposalId}/outcomes`, data),
  updateOutcome: (proposalId, outcomeId, data) => api.put(`/api/proposals/${proposalId}/outcomes/${outcomeId}`, data),
  deleteOutcome: (proposalId, outcomeId) => api.delete(`/api/proposals/${proposalId}/outcomes/${outcomeId}`),
  getOutcomeDeliverables: (proposalId, outcomeId) => api.get(`/api/proposals/${proposalId}/outcomes/${outcomeId}/deliverables`),
  addOutcomeDeliverable: (proposalId, outcomeId, data) => api.post(`/api/proposals/${proposalId}/outcomes/${outcomeId}/deliverables`, data),
  updateOutcomeDeliverable: (proposalId, outcomeId, deliverableId, data) =>
    api.put(`/api/proposals/${proposalId}/outcomes/${outcomeId}/deliverables/${deliverableId}`, data),
  deleteOutcomeDeliverable: (proposalId, outcomeId, deliverableId) => api.delete(`/api/proposals/${proposalId}/outcomes/${outcomeId}/deliverables/${deliverableId}`),
  // PRD-020 F5 — proposal economics (revenue + seller-only Estimated Cost + margin).
  economics: (proposalId) => api.get(`/api/proposals/${proposalId}/economics`),
  // PRD-020 F5 — per-Outcome Estimated Cost lines (catalog-linked).
  addCostEstimate: (proposalId, outcomeId, data) => api.post(`/api/proposals/${proposalId}/outcomes/${outcomeId}/cost-estimates`, data),
  updateCostEstimate: (proposalId, outcomeId, estimateId, data) => api.put(`/api/proposals/${proposalId}/outcomes/${outcomeId}/cost-estimates/${estimateId}`, data),
  deleteCostEstimate: (proposalId, outcomeId, estimateId) => api.delete(`/api/proposals/${proposalId}/outcomes/${outcomeId}/cost-estimates/${estimateId}`),
  attachCostType: (proposalId, data) => api.post(`/api/proposals/${proposalId}/cost-types`, data),
  updateCostType: (proposalId, costTypeId, data) => api.put(`/api/proposals/${proposalId}/cost-types/${costTypeId}`, data),
  removeCostType: (proposalId, costTypeId) => api.delete(`/api/proposals/${proposalId}/cost-types/${costTypeId}`),
  // PRD-005 WU 5-2: move a drafting Proposal to a different Connection.
  retarget: (proposalId, { newConnectionId, idempotencyKey }) =>
    api.post(`/api/proposals/${proposalId}/retarget`, {
      new_connection_id: newConnectionId,
      idempotency_key: idempotencyKey,
    }),
};

// Agreements API
export const agreementsApi = {
  list: (params) => api.get('/api/agreements', { params }),
  get: (id) => api.get(`/api/agreements/${id}`),
  portfolio: () => api.get('/api/agreements/analytics/portfolio'),
  updateDeliverable: (agreementId, deliverableId, data) =>
    api.put(`/api/agreements/${agreementId}/deliverables/${deliverableId}`, data),
  amend: (agreementId) => api.post(`/api/agreements/${agreementId}/amend`),
  addBooking: (agreementId, data) => api.post(`/api/agreements/${agreementId}/bookings`, data),
  updateBooking: (agreementId, bookingId, data) =>
    api.put(`/api/agreements/${agreementId}/bookings/${bookingId}`, data),
  deleteBooking: (agreementId, bookingId) =>
    api.delete(`/api/agreements/${agreementId}/bookings/${bookingId}`),
  // PRD-011: Completion Attestation read + recovery-path regenerate.
  getAttestation: (agreementId) =>
    api.get(`/api/agreements/${agreementId}/attestation`),
  regenerateAttestation: (agreementId) =>
    api.post(`/api/agreements/${agreementId}/attestation/regenerate`),
};

// Cost Input Types API
export const costInputTypesApi = {
  list: (params) => api.get('/api/cost-input-types', { params }),
  get: (id) => api.get(`/api/cost-input-types/${id}`),
  // PRD-023 FR-5 — near-match dedup lookup before a tenant create.
  match: (name) => api.get('/api/cost-input-types/match', { params: { name } }),
  create: (data) => api.post('/api/cost-input-types', data),
  update: (id, data) => api.put(`/api/cost-input-types/${id}`, data),
  delete: (id) => api.delete(`/api/cost-input-types/${id}`),
  // PRD-025/PRD-027 — effective-dated rate windows (parent Cost Type → child rates).
  listRates: (id) => api.get(`/api/cost-input-types/${id}/rates`),
  addRate: (id, data) => api.post(`/api/cost-input-types/${id}/rates`, data),
  editRate: (id, rateId, data) => api.patch(`/api/cost-input-types/${id}/rates/${rateId}`, data),
  removeRate: (id, rateId) => api.delete(`/api/cost-input-types/${id}/rates/${rateId}`),
  // PRD-027 WU-F — evergreen XOR windowed mode conversion.
  setRateMode: (id, data) => api.patch(`/api/cost-input-types/${id}/rate-mode`, data),
};

// PRD-014 — P&L surface (server-computed) + share-unit-economics toggle +
// PRD-013 allocation endpoints.
export const pnlApi = {
  get: (agreementId) => api.get(`/api/agreements/${agreementId}/pnl`),
  setShareUnitEconomics: (agreementId, shared) =>
    api.patch(`/api/agreements/${agreementId}`, { unit_economics_shared_with_buyer: shared }),
};

// PRD-030 Phase 2 — evidence files + verification runs (delivery checks).
export const evidenceFilesApi = {
  list: (agreementId) => api.get(`/api/agreements/${agreementId}/evidence-files`),
  upload: (agreementId, file, deliverableId) => {
    const form = new FormData();
    form.append('file', file);
    if (deliverableId) form.append('deliverable_id', deliverableId);
    return api.post(`/api/agreements/${agreementId}/evidence-files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  remove: (agreementId, fileId) => api.delete(`/api/agreements/${agreementId}/evidence-files/${fileId}`),
};

// PRD-030 WU-12 — the before-signing outcome-completeness check.
export const outcomeCompletenessApi = {
  get: (proposalId) => api.get(`/api/proposals/${proposalId}/lint`),
  run: (proposalId) => api.post(`/api/proposals/${proposalId}/lint`),
  acknowledge: (proposalId, reportId) =>
    api.post(`/api/proposals/${proposalId}/lint/acknowledge`, { report_id: reportId }),
};

export const verificationRunsApi = {
  list: (agreementId) => api.get(`/api/agreements/${agreementId}/verification-runs`),
  trigger: (agreementId, body = {}) => api.post(`/api/agreements/${agreementId}/verification-runs`, body),
  startNow: (agreementId, runId) => api.post(`/api/agreements/${agreementId}/verification-runs/${runId}/start`),
  confirm: (agreementId, runId) => api.post(`/api/agreements/${agreementId}/verification-runs/${runId}/confirm`),
};

// PRD-030 WU-13 — agency→client progress summaries.
export const progressUpdatesApi = {
  list: (agreementId) => api.get(`/api/agreements/${agreementId}/progress-updates`),
  send: (agreementId, note) => api.post(`/api/agreements/${agreementId}/progress-updates`, { note }),
};

export const verificationAttentionApi = {
  get: () => api.get('/api/workspaces/current/verification-attention'),
};

export const allocationsApi = {
  get: (agreementId) => api.get(`/api/agreements/${agreementId}/allocations`),
  setAllocation: (agreementId, outcomeId, allocationCents) =>
    api.post(`/api/agreements/${agreementId}/outcomes/${outcomeId}/allocation`, { allocation_cents: allocationCents }),
  reset: (agreementId) => api.post(`/api/agreements/${agreementId}/allocations/reset`),
};

// Outcome Spec block template library (PRD-015) — read-only v1 ad/media library
export const outcomeSpecTemplatesApi = {
  list: (params) => api.get('/api/outcome-spec-templates', { params }),
};

// Cost Type Catalog API (PRD-012) — read-only platform-curated catalog
export const costTypeCatalogApi = {
  list: (params) => api.get('/api/cost-type-catalog', { params }),
};

// Cost Type Suggestions API (PRD-012) — audit-logged "request to add"
export const costTypeSuggestionsApi = {
  create: (data) => api.post('/api/cost-type-suggestions', data),
  // operator-gated review queue (reason >= 10 chars); no in-app reviewer UI in PRD-012
  list: (params) => api.get('/api/cost-type-suggestions', { params }),
};

// Consumption Entries API
export const consumptionEntriesApi = {
  list: (agreementId, params) => api.get(`/api/consumption-entries/${agreementId}`, { params }),
  create: (agreementId, data) => api.post(`/api/consumption-entries/${agreementId}`, data),
  delete: (agreementId, entryId) => api.delete(`/api/consumption-entries/${agreementId}/${entryId}`),
};

// Outputs API
export const outputsApi = {
  getLink: (proposalId) => api.get(`/api/outputs/${proposalId}/link`),
  regenerateLink: (proposalId) => api.post(`/api/outputs/${proposalId}/link/regenerate`),
  getPdfUrl: (proposalId) => `${API_BASE_URL}/api/outputs/${proposalId}/pdf`,
  getPreviewUrl: (proposalId) => `${API_BASE_URL}/api/outputs/${proposalId}/preview`,
  getData: (proposalId) => api.get(`/api/outputs/${proposalId}/data`),
};

// Units of Measure API
export const unitsOfMeasureApi = {
  list: (params) => api.get('/api/units-of-measure', { params }),
  get: (id) => api.get(`/api/units-of-measure/${id}`),
  create: (data) => api.post('/api/units-of-measure', data),
  update: (id, data) => api.put(`/api/units-of-measure/${id}`, data),
  delete: (id) => api.delete(`/api/units-of-measure/${id}`),
  dataTypes: () => api.get('/api/units-of-measure/data-types'),
  // PRD-010 F-SP-3: suggest a UoM that isn't in the catalog yet.
  suggest: (name) => api.post('/api/units-of-measure/suggestions', { name }),
};

// Delivery Measures API
export const deliveryMeasuresApi = {
  list: (params) => api.get('/api/delivery-measures', { params }),
  get: (id) => api.get(`/api/delivery-measures/${id}`),
  create: (data) => api.post('/api/delivery-measures', data),
  update: (id, data) => api.put(`/api/delivery-measures/${id}`, data),
  delete: (id) => api.delete(`/api/delivery-measures/${id}`),
};

// API Keys API
export const apiKeysApi = {
  list: () => api.get('/api/api-keys'),
  create: (data) => api.post('/api/api-keys', data),
  rotate: (id) => api.post(`/api/api-keys/${id}/rotate`),
  delete: (id) => api.delete(`/api/api-keys/${id}`),
};

// Outcome Domains API
export const outcomeDomainsApi = {
  list: (params) => api.get('/api/outcome-domains', { params }),
  get: (id) => api.get(`/api/outcome-domains/${id}`),
  create: (data) => api.post('/api/outcome-domains', data),
  update: (id, data) => api.put(`/api/outcome-domains/${id}`, data),
  delete: (id) => api.delete(`/api/outcome-domains/${id}`),
};

// Admin API
export const adminApi = {
  previewUomReassign: (data) => api.post('/api/admin/bulk-reassign/units-of-measure/preview', data),
  executeUomReassign: (data) => api.post('/api/admin/bulk-reassign/units-of-measure/execute', data),
  previewDomainReassign: (data) => api.post('/api/admin/bulk-reassign/outcome-domains/preview', data),
  executeDomainReassign: (data) => api.post('/api/admin/bulk-reassign/outcome-domains/execute', data),
};

// PRD-008 — Platform Operator Console.
//
// Every endpoint here goes through requirePlatformOperator on the
// backend. The frontend route guard (PlatformOperatorRoute) keeps non-
// operators from even reaching these calls.
export const internalApi = {
  ping: () => api.get('/api/internal/ping'),
  listWorkspaces: (params) => api.get('/api/internal/workspaces', { params }),
  // The reason gate (≥10 chars) lives server-side; we pass it via the
  // ?reason= query string because GET-with-body is non-spec.
  getWorkspace: (id, reason) =>
    api.get(`/api/internal/workspaces/${id}`, { params: { reason } }),
  dashboard: (params) => api.get('/api/internal/dashboard', { params }),
  audit: (params) => api.get('/api/internal/audit', { params }),
  startImpersonation: (workspaceId, reason) =>
    api.post(`/api/internal/workspaces/${workspaceId}/impersonation-sessions`, { reason }),
  endImpersonation: (sessionId) =>
    api.post(`/api/internal/impersonation-sessions/${sessionId}/end`),
};

// Reports API
export const reportsApi = {
  schema: () => api.get('/api/reports/schema'),
  schemaForType: (reportType) => api.get(`/api/reports/schema/${reportType}`),
  joinableTables: (reportType, existingTables) => {
    const params = existingTables ? { existingTables: existingTables.join(',') } : {};
    return api.get(`/api/reports/schema/joinable/${reportType}`, { params });
  },
  query: (data) => api.post('/api/reports/query', data),
  exportFile: (data, format) =>
    api.post('/api/reports/export', { ...data, format }, { responseType: 'blob' }),
  listSaved: () => api.get('/api/reports/saved'),
  createSaved: (data) => api.post('/api/reports/saved', data),
  updateSaved: (id, data) => api.put(`/api/reports/saved/${id}`, data),
  deleteSaved: (id) => api.delete(`/api/reports/saved/${id}`),
};

export default api;
